import type { ModelDefinition } from "@/models.js";

export const xiaomiModels = [
	{
		id: "mimo-v2.6-pro",
		name: "MiMo V2.6 Pro",
		description:
			"Xiaomi's flagship trillion-parameter omni-modal reasoning model with 1M context, built for complex, long-horizon agentic work.",
		family: "xiaomi",
		releasedAt: new Date("2026-09-22"),
		providers: [
			{
				providerId: "xiaomi" as const,
				externalId: "mimo-v2.6-pro",
				inputPrice: "0.435e-6",
				outputPrice: "0.87e-6",
				cachedInputPrice: "0.0036e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				maxTemperature: 1.5,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: true,
				audio: true,
				tools: true,
				supportedToolChoices: ["auto"],
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "XiaomiMiMo/MiMo-V2.6-Pro",
				inputPrice: "0.435e-6",
				outputPrice: "0.87e-6",
				cachedInputPrice: "0.0036e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "mimo-v2.6-flash",
		name: "MiMo V2.6 Flash",
		description:
			"Xiaomi's low-cost omni-modal reasoning model with 1M context, balancing intelligence and price for high-volume workloads.",
		family: "xiaomi",
		releasedAt: new Date("2026-09-22"),
		providers: [
			{
				providerId: "xiaomi" as const,
				externalId: "mimo-v2.6-flash",
				inputPrice: "0.14e-6",
				outputPrice: "0.28e-6",
				cachedInputPrice: "0.0028e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				maxTemperature: 1.5,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: true,
				audio: true,
				tools: true,
				supportedToolChoices: ["auto"],
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "XiaomiMiMo/MiMo-V2.6-Flash",
				inputPrice: "0.14e-6",
				outputPrice: "0.28e-6",
				cachedInputPrice: "0.0028e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 65536,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				audio: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "mimo-v2.5-pro",
		name: "MiMo V2.5 Pro",
		description:
			"Xiaomi's flagship 1T-parameter model with 42B activations, 1M ultra-long context, and deep thinking capabilities. Performs comparably to Claude Opus 4.6 in agent scenarios.",
		family: "xiaomi",
		releasedAt: new Date("2026-04-23"),
		providers: [
			{
				providerId: "xiaomi" as const,
				externalId: "mimo-v2.5-pro",
				// Xiaomi auto-routes this ID to mimo-v2.6-pro (billed at the new
				// model's rates) from Beijing 2026-10-14 18:00 and retires it at
				// 2026-10-21 10:00. Deactivate at the switch so requests error
				// instead of silently routing to a different model.
				deprecatedAt: new Date("2026-10-09"),
				deactivatedAt: new Date("2026-10-14T10:00:00Z"),
				inputPrice: "0.435e-6",
				outputPrice: "0.87e-6",
				cachedInputPrice: "0.0036e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Xiaomi accepts low/medium/high natively and rejects the other
				// tiers with a 400; `none` maps to the binary thinking disable.
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "xiaomimimo/mimo-v2.5-pro",
				inputPrice: "0.522e-6",
				outputPrice: "1.044e-6",
				cachedInputPrice: "0.0043e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "minimal", "low", "medium", "high"],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "XiaomiMiMo/MiMo-V2.5-Pro",
				// DeepInfra retires this deployment and redirects it to the
				// V2.6 model, which is a different model with different prices.
				deactivatedAt: new Date("2026-09-29"),
				inputPrice: "1e-6",
				outputPrice: "3e-6",
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 16384,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				// With thinking enabled by default, DeepInfra emits requested tool
				// calls only as reasoning text and leaves tool_calls empty.
				tools: false,
				// DeepInfra likewise puts structured output in reasoning text and
				// returns empty content unless thinking is explicitly disabled.
				jsonOutput: false,
			},
			{
				providerId: "tencent",
				externalId: "mimo-v2.5-pro",
				// TokenHub retires this model at 2026-10-20 23:59:59 Beijing time.
				deactivatedAt: new Date("2026-10-20T16:00:00Z"),
				inputPrice: "0.435e-6",
				cachedInputPrice: "0.0036e-6",
				outputPrice: "0.87e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Same tiers the direct Xiaomi deployment accepts: minimal, xhigh
				// and max 400 here too.
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: false,
				tools: true,
				jsonOutput: true,
				// json_object is reliable, but json_schema is not: the response
				// comes back truncated or with thinking leaking into the JSON
				// body ('{"is_user', '{The user wants'), so it fails to parse on
				// most attempts.
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "mimo-v2-pro",
		name: "MiMo V2 Pro",
		description:
			"Xiaomi's 1T-parameter model with 42B active parameters and 1M context window using hybrid Global Attention + SWA architecture.",
		family: "xiaomi",
		releasedAt: new Date("2026-03-18"),
		providers: [
			{
				deactivatedAt: new Date("2026-06-30"),
				providerId: "xiaomi" as const,
				externalId: "mimo-v2-pro",
				inputPrice: "1e-6",
				outputPrice: "3e-6",
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "mimo-v2.5",
		name: "MiMo V2.5",
		description:
			"Xiaomi's full-modal perception model supporting native understanding of images, videos, audio, and text with 1M context. Agent performance comparable to MiMo V2.5 Pro.",
		family: "xiaomi",
		releasedAt: new Date("2026-04-23"),
		providers: [
			{
				providerId: "xiaomi" as const,
				externalId: "mimo-v2.5",
				// Xiaomi auto-routes this ID to mimo-v2.6-flash (billed at the new
				// model's rates) from Beijing 2026-10-14 18:00 and retires it at
				// 2026-10-21 10:00. Deactivate at the switch so requests error
				// instead of silently routing to a different model.
				deprecatedAt: new Date("2026-10-09"),
				deactivatedAt: new Date("2026-10-14T10:00:00Z"),
				inputPrice: "0.14e-6",
				outputPrice: "0.28e-6",
				cachedInputPrice: "0.0028e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Xiaomi accepts low/medium/high natively and rejects the other
				// tiers with a 400; `none` maps to the binary thinking disable.
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "xiaomimimo/mimo-v2.5",
				inputPrice: "0.168e-6",
				outputPrice: "0.336e-6",
				cachedInputPrice: "0.0034e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "XiaomiMiMo/MiMo-V2.5",
				// DeepInfra retires this deployment and redirects it to the
				// V2.6 model, which is a different model with different prices.
				deactivatedAt: new Date("2026-09-29"),
				inputPrice: "0.4e-6",
				outputPrice: "2e-6",
				cachedInputPrice: "0.08e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 16384,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				audio: true,
				// DeepInfra omits reasoning_content when the model immediately emits
				// a tool call, while returning it for ordinary reasoning requests.
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "mimo-v2-omni",
		name: "MiMo V2 Omni",
		description:
			"Xiaomi's multimodal model supporting text, vision, and speech modalities with 256K context window.",
		family: "xiaomi",
		releasedAt: new Date("2026-03-18"),
		providers: [
			{
				deactivatedAt: new Date("2026-06-30"),
				providerId: "xiaomi" as const,
				externalId: "mimo-v2-omni",
				inputPrice: "0.4e-6",
				outputPrice: "2e-6",
				cachedInputPrice: "0.08e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 131072,
				streaming: true,
				reasoning: false,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "mimo-v2-flash",
		name: "MiMo V2 Flash",
		description:
			"Xiaomi's high-efficiency inference model with hybrid architecture, 3 MTP layers for 2.5-3.7x faster inference, and 256K context.",
		family: "xiaomi",
		releasedAt: new Date("2025-12-16"),
		providers: [
			{
				providerId: "xiaomi" as const,
				externalId: "mimo-v2-flash",
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				cachedInputPrice: "0.02e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: undefined,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit" as const,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Xiaomi retires mimo-v2-flash and auto-forwards requests to
				// mimo-v2.5 (billed as the new model) at Beijing 2026-06-18 00:00.
				// Deactivate at the switch so requests error instead of silently
				// routing to a different model.
				deactivatedAt: new Date("2026-06-17T16:00:00Z"),
			},
		],
	},
] as const satisfies ModelDefinition[];
